import React from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { getPSPContent, normalizeKeyword } from "pages/PSPPage/ContentMap";
import ModuleLayout from "components/organisms/ModuleLayout/ModuleLayout";
import { Page } from "components/core/Page";
import NotFoundErrorPage from "pages/ErrorPage/ErrorPage";
import { getMetadata } from "./utils/getMetadata";
import {
	buildReviewSchema,
	buildPageSchema,
	buildPSPUrl
} from "./utils/buildSchema";

export const VALID_KEYWORDS = [
	"24-hour-storage-units", "climate-controlled-storage-units",
	"drive-up-storage-units", "portable-storage-units",
	"storage-units-with-electricity", "wine-storage-units",
	"college-storage-units", "business-storage-units",
	"military-storage-units", "atv-storage-units", "bike-storage-units",
	"boat-storage-units", "car-storage-units", "dirt-bike-storage-units",
	"golf-cart-storage-units", "jet-ski-storage-units", "kayak-storage-units",
	"motorcycle-storage-units", "rv-storage-units",
	"storage-units-with-free-truck-rental", "vehicle-storage-units",
	"what-are-storage-units"
];

function PSPPage(props) {
	const { handleSearch } = props;
	const { keyword: keywordParam } = useParams();
	const normalizedKeyword = normalizeKeyword(keywordParam);
	if (!VALID_KEYWORDS.includes(keywordParam)) {
		return <NotFoundErrorPage statusCode={404} />;
	}
	const pageContent = getPSPContent(normalizedKeyword);
	if (!pageContent) {
		return <NotFoundErrorPage {...props} />;
	}

	const metadata = getMetadata(normalizedKeyword);
	const reviewSchema = buildReviewSchema(pageContent);
	const canonicalUrl = buildPSPUrl(keywordParam);
	const heroImageUrl = `https://www.storage.com/images/psp/hero/${normalizedKeyword}-hero.webp`;
	const breadcrumbUrl = `/storage-solutions/${keywordParam}`;
	const schema = buildPageSchema(
		keywordParam,
		normalizedKeyword,
		pageContent.type,
		reviewSchema,
		metadata
	);
	const pageSchema = JSON.stringify(schema);

	return (
		<Page className="psp-page">
			<Helmet>
				<title>{metadata?.title}</title>
				<link
					rel="canonical"
					href={canonicalUrl}
				/>
				<meta
					name="description"
					content={metadata?.description}
				/>
				<meta
					property="og:title"
					content={metadata?.title}
				/>
				<meta
					property="og:description"
					content={metadata?.description}
				/>
				<meta
					property="og:url"
					content={canonicalUrl}
				/>
				<meta
					property="og:type"
					content="website"
				/>
				<meta
					property="og:image"
					content={heroImageUrl}
				/>
				<meta
					property="og:site_name"
					content="Storage.com"
				/>
				<meta
					name="twitter:card"
					content="summary_large_image"
				/>
				<meta
					name="twitter:site"
					content="@storage"
				/>
				<meta
					name="twitter:title"
					content={metadata?.title}
				/>
				<meta
					name="twitter:description"
					content={metadata?.description}
				/>
				<meta
					name="twitter:image"
					content={heroImageUrl}
				/>
			</Helmet>
			<ModuleLayout
				key={normalizedKeyword}
				content={pageContent}
				pageSchema={pageSchema}
				handleSearch={handleSearch}
				breadcrumbUrl={breadcrumbUrl}
			/>
		</Page>
	);
}

PSPPage.propTypes = {
	handleSearch: PropTypes.func
};

export default PSPPage;
